/**
 * Utilidades para formateo de fechas en el frontend
 */

/**
 * Formatea una fecha al formato dd-MM-yyyy
 * @param {Date|string} fecha - La fecha a formatear
 * @returns {string} La fecha formateada como dd-MM-yyyy
 */
export function formatearFecha(fecha) {
  if (!fecha) return ''
  
  try {
    const date = fecha instanceof Date ? fecha : new Date(fecha)
    
    if (isNaN(date.getTime())) return ''
    
    const dia = date.getDate().toString().padStart(2, '0')
    const mes = (date.getMonth() + 1).toString().padStart(2, '0')
    const año = date.getFullYear()
    
    return `${dia}-${mes}-${año}`
  } catch (error) {
    console.error('Error al formatear fecha:', error)
    return ''
  }
}

/**
 * Formatea una fecha al formato dd/MM/yyyy
 * @param {Date|string} fecha - La fecha a formatear
 * @returns {string} La fecha formateada como dd/MM/yyyy
 */
export function formatearFechaConSlash(fecha) {
  if (!fecha) return ''
  
  try {
    const date = fecha instanceof Date ? fecha : new Date(fecha)
    
    if (isNaN(date.getTime())) return ''
    
    const dia = date.getDate().toString().padStart(2, '0')
    const mes = (date.getMonth() + 1).toString().padStart(2, '0')
    const año = date.getFullYear()
    
    return `${dia}/${mes}/${año}`
  } catch (error) {
    console.error('Error al formatear fecha:', error)
    return ''
  }
}

/**
 * Formatea una fecha con hora al formato dd-MM-yyyy HH:mm
 * @param {Date|string} fecha - La fecha a formatear
 * @returns {string} La fecha y hora formateada
 */
export function formatearFechaHora(fecha) {
  if (!fecha) return ''
  
  try {
    const date = fecha instanceof Date ? fecha : new Date(fecha)
    
    if (isNaN(date.getTime())) return ''
    
    const dia = date.getDate().toString().padStart(2, '0')
    const mes = (date.getMonth() + 1).toString().padStart(2, '0')
    const año = date.getFullYear()
    const hora = date.getHours().toString().padStart(2, '0')
    const minutos = date.getMinutes().toString().padStart(2, '0')
    
    return `${dia}-${mes}-${año} ${hora}:${minutos}`
  } catch (error) {
    console.error('Error al formatear fecha y hora:', error)
    return ''
  }
}

/**
 * Convierte una fecha del formato yyyy-MM-dd a dd-MM-yyyy
 * @param {string} fechaString - La fecha en formato yyyy-MM-dd
 * @returns {string} La fecha en formato dd-MM-yyyy
 */
export function convertirFechaBackend(fechaString) {
  if (!fechaString) return ''
  
  try {
    // Si ya viene en formato yyyy-MM-dd del backend
    if (fechaString.includes('-') && fechaString.length === 10) {
      const [año, mes, dia] = fechaString.split('-')
      return `${dia}-${mes}-${año}`
    }
    
    // Si es una fecha con timestamp o ISO
    const date = new Date(fechaString)
    if (isNaN(date.getTime())) return fechaString
    
    const dia = date.getDate().toString().padStart(2, '0')
    const mes = (date.getMonth() + 1).toString().padStart(2, '0')
    const año = date.getFullYear()
    
    return `${dia}-${mes}-${año}`
  } catch (error) {
    console.error('Error al convertir fecha:', error)
    return fechaString
  }
}

/**
 * Parsea una fecha de formato dd-MM-yyyy o dd/MM/yyyy a Date
 * @param {string} fechaString - La fecha en formato dd-MM-yyyy o dd/MM/yyyy
 * @returns {Date|null} El objeto Date o null si es inválida
 */
export function parsearFecha(fechaString) {
  if (!fechaString || fechaString.trim() === '') return null
  
  try {
    // Intentar con formato dd-MM-yyyy o dd/MM/yyyy
    const separador = fechaString.includes('/') ? '/' : '-'
    const regex = new RegExp(`^(\\d{2})${separador.replace(/\//, '\\/')}(\\d{2})${separador.replace(/\//, '\\/')}(\\d{4})$`)
    const match = fechaString.match(regex)
    
    if (match) {
      const [, dia, mes, año] = match
      const fecha = new Date(año, mes - 1, dia)
      
      // Verificar que la fecha es válida
      if (fecha.getDate() == dia && fecha.getMonth() == mes - 1 && fecha.getFullYear() == año) {
        return fecha
      }
    }
    
    return null
  } catch (error) {
    console.error('Error al parsear fecha:', error)
    return null
  }
}

/**
 * Normaliza una fecha para que solo tenga día, mes y año (sin hora)
 * @param {Date} fecha - La fecha a normalizar
 * @returns {Date|null} La fecha normalizada
 */
export function normalizarFecha(fecha) {
  if (!fecha) return null
  
  try {
    const date = fecha instanceof Date ? fecha : new Date(fecha)
    if (isNaN(date.getTime())) return null
    
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  } catch (error) {
    console.error('Error al normalizar fecha:', error)
    return null
  }
}

